"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  DEFAULT_THEME,
  isTheme,
  readThemeCookie,
  writeThemeCookie,
  type Theme,
} from "@/lib/theme";

interface ThemeContextType {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType>({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

/**
 * Theme state for the toggle controls and for the imperative consumers that
 * can't read CSS (the three map surfaces).
 *
 * Deliberately NOT the styling mechanism: all ~3,100 token utilities restyle
 * from `data-theme` on <html> via CSS custom properties, so flipping the theme
 * costs one attribute write and zero React re-renders of the tree. Anything
 * that subscribes here should be something CSS genuinely cannot reach.
 *
 * The attribute is already correct on arrival — the boot script in
 * src/app/layout.tsx sets it before first paint — so this provider only has to
 * mirror it into state and own subsequent writes.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(DEFAULT_THEME);
  const { user } = useAuth();

  // Adopt whatever the boot script decided, rather than assuming the default —
  // otherwise a light-mode user's first client render disagrees with the DOM.
  useEffect(() => {
    const fromDom = document.documentElement.dataset.theme;
    setThemeState(isTheme(fromDom) ? fromDom : readThemeCookie() ?? DEFAULT_THEME);
  }, []);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState((prev) => {
        if (prev === next) return prev;

        const root = document.documentElement;
        // Suppress transitions for one frame. Without this, every element with
        // a colour transition crossfades at once, which visibly janks on the
        // map surfaces where thousands of nodes are in flight.
        root.classList.add("theme-switching");
        root.dataset.theme = next;
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => root.classList.remove("theme-switching"));
        });

        writeThemeCookie(next);
        return next;
      });
    },
    [],
  );

  // Mirror to the account so the preference follows the user across devices.
  // Same two-write pattern as the role in src/lib/auth/role.ts: user_metadata
  // is the cheap read source (it rides along with the session — no query), and
  // the profiles row is the queryable mirror.
  useEffect(() => {
    if (!user) return;
    if (user.user_metadata?.theme === theme) return;

    const supabase = createClient();
    if (!supabase) return;

    let cancelled = false;
    // Debounced so rapid toggling doesn't fire a write per click.
    const timer = setTimeout(() => {
      void (async () => {
        try {
          await supabase.auth.updateUser({ data: { theme } });
          if (cancelled) return;
          await supabase.from("profiles").upsert({ id: user.id, theme }, { onConflict: "id" });
        } catch {
          // Best-effort: the cookie already holds the preference for this
          // browser. Not worth surfacing an error over a display setting.
        }
      })();
    }, 800);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [user, theme]);

  // On sign-in, adopt the account's stored theme when this browser has no
  // explicit choice of its own — a new device should inherit the preference,
  // but a deliberate local toggle should win over a stale remote value.
  useEffect(() => {
    if (!user) return;
    if (readThemeCookie()) return;
    const remote = user.user_metadata?.theme;
    if (isTheme(remote)) setTheme(remote);
  }, [user, setTheme]);

  const toggleTheme = useCallback(
    () => setTheme(theme === "dark" ? "light" : "dark"),
    [theme, setTheme],
  );

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
